import { getElementBounds } from './elementBounds';

/**
 * Clamp crop rect to image bounds (natural pixels).
 */
export function clampCropToImage(naturalW, naturalH, r) {
  let x = Math.max(0, Math.min(r.x, naturalW - 1));
  let y = Math.max(0, Math.min(r.y, naturalH - 1));
  let w = Math.max(0, r.w);
  let h = Math.max(0, r.h);
  w = Math.min(w, naturalW - x);
  h = Math.min(h, naturalH - y);
  return { x, y, w, h };
}

function shiftElement(el, cx, cy) {
  switch (el.type) {
    case 'rect':
    case 'blur':
      return { ...el, x: el.x - cx, y: el.y - cy };
    case 'text':
      return { ...el, x: el.x - cx, y: el.y - cy };
    case 'arrow':
      return {
        ...el,
        x1: el.x1 - cx,
        y1: el.y1 - cy,
        x2: el.x2 - cx,
        y2: el.y2 - cy,
      };
    case 'path':
      return {
        ...el,
        points: el.points.map((p) => ({ x: p.x - cx, y: p.y - cy })),
      };
    default:
      return el;
  }
}

function rectsOverlap(a, b) {
  return !(a.x + a.w <= b.x || a.x >= b.x + b.w || a.y + a.h <= b.y || a.y >= b.y + b.h);
}

/**
 * Shift annotations into cropped coordinates and drop anything fully outside the new canvas.
 * @param {object[]} elements
 * @param {{ x: number; y: number; w: number; h: number }} crop
 * @param {CanvasRenderingContext2D | null} measureCtx
 */
export function shiftElementsAfterCrop(elements, crop, measureCtx) {
  const { x: cx, y: cy, w: vw, h: vh } = crop;
  const viewport = { x: 0, y: 0, w: vw, h: vh };
  return elements
    .map((el) => shiftElement(el, cx, cy))
    .filter((el) => {
      const b = getElementBounds(el, measureCtx);
      return rectsOverlap(b, viewport);
    });
}

/**
 * @param {HTMLImageElement} img
 * @param {{ x: number; y: number; w: number; h: number }} crop
 * @returns {Promise<HTMLImageElement>}
 */
export function rasterizeCroppedImage(img, crop) {
  const { x, y, w, h } = crop;
  const cw = Math.max(1, Math.round(w));
  const ch = Math.max(1, Math.round(h));
  const canvas = document.createElement('canvas');
  canvas.width = cw;
  canvas.height = ch;
  const ctx = canvas.getContext('2d');
  if (!ctx) return Promise.reject(new Error('No context'));
  try {
    ctx.drawImage(img, x, y, w, h, 0, 0, cw, ch);
  } catch (err) {
    return Promise.reject(err);
  }
  return new Promise((resolve, reject) => {
    const out = new Image();
    out.onload = () => resolve(out);
    out.onerror = () => reject(new Error('Image decode failed'));
    out.src = canvas.toDataURL('image/png');
  });
}
